import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { formatDate } from '../../../utils/dateTime';
import {
  Gavel,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  FileText,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  created: { label: 'Created', color: 'bg-slate-100 text-slate-700', icon: Clock },
  sent: { label: 'Sent to Bureau', color: 'bg-emerald-100 text-emerald-700', icon: ArrowRight },
  pending_response: { label: 'Awaiting Response', color: 'bg-amber-100 text-amber-700', icon: Clock },
  response_received: { label: 'Response Received', color: 'bg-amber-100 text-amber-700', icon: FileText },
  resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-500', icon: CheckCircle },
};

/**
 * Managed Client Dispute Status Page - Phase 7
 * 
 * View-only dispute tracking for managed clients.
 * Shows simplified status without internal workflow details.
 */
const ManagedClientDisputesPage = () => {
  const { token } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/managed/disputes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisputes(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch disputes:', err);
      setError(err.response?.data?.detail || 'Failed to load dispute status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchDisputes();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const activeDisputes = disputes.filter(d => !['resolved', 'closed'].includes(d.dispute_status));
  const resolvedDisputes = disputes.filter(d => ['resolved', 'closed'].includes(d.dispute_status));

  return (
    <div className="space-y-6" data-testid="managed-client-disputes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Dispute Status</h1>
          <p className="text-slate-500 mt-1">Track the progress of your disputes</p>
        </div>
        <Button onClick={fetchDisputes} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Gavel className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Disputes</p>
              <p className="text-2xl font-bold text-stone-900">{disputes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Clock className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">In Progress</p>
              <p className="text-2xl font-bold text-amber-600">{activeDisputes.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Resolved</p>
              <p className="text-2xl font-bold text-emerald-600">{resolvedDisputes.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Disputes */}
      {activeDisputes.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Active Disputes</h2>
          <div className="space-y-4">
            {activeDisputes.map((dispute) => {
              const status = statusConfig[dispute.dispute_status] || statusConfig.created;
              const StatusIcon = status.icon;
              return (
                <div 
                  key={dispute.dispute_id}
                  className="bg-white rounded-xl border border-slate-200 p-5"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-stone-900">
                        Round {dispute.round || 1} - {dispute.bureau || 'Multiple Bureaus'}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {dispute.items?.length || 0} items being disputed
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                      <StatusIcon size={14} />
                      {status.label}
                    </span>
                  </div>
                  
                  {/* Progress Timeline */}
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                    <div className={`w-3 h-3 rounded-full ${dispute.dispute_status ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <div className={`flex-1 h-1 ${['sent', 'pending_response', 'response_received', 'resolved'].includes(dispute.dispute_status) ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                    <div className={`w-3 h-3 rounded-full ${['pending_response', 'response_received', 'resolved'].includes(dispute.dispute_status) ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <div className={`flex-1 h-1 ${['response_received', 'resolved'].includes(dispute.dispute_status) ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                    <div className={`w-3 h-3 rounded-full ${dispute.dispute_status === 'resolved' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>Created</span>
                    <span>Sent</span>
                    <span>Resolved</span>
                  </div>
                  
                  {dispute.created_at && (
                    <p className="text-xs text-slate-400 mt-4">
                      Started on {formatDate(dispute.created_at)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resolved Disputes */}
      {resolvedDisputes.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Resolved Disputes</h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {resolvedDisputes.map((dispute) => (
              <div 
                key={dispute.dispute_id}
                className="p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-stone-900">
                    Round {dispute.round || 1} - {dispute.bureau || 'Multiple Bureaus'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {dispute.items?.length || 0} items
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                  <CheckCircle size={14} />
                  Resolved
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {disputes.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Gavel size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No Disputes Yet</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Your specialist is working on your credit report. Disputes will appear here once they are created.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-emerald-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-emerald-800">How Disputes Work</h4>
            <p className="text-sm text-emerald-700 mt-1">
              Your specialist identifies negative items on your credit report and sends dispute letters 
              to the credit bureaus. Each bureau has 30-45 days to investigate and respond. 
              We track every step of this process for you.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagedClientDisputesPage;
